import React, { useEffect, useState } from "react";
import {
  createUserWithEmailAndPassword,
  fetchSignInMethodsForEmail,
  GoogleAuthProvider,
  signInWithEmailAndPassword,
  signInWithPopup,
  signOut,
} from "firebase/auth";
import {
  collection,
  doc,
  getDoc,
  getDocs,
  limit,
  query,
  serverTimestamp,
  setDoc,
  where,
} from "firebase/firestore";
import { motion } from "framer-motion";
import { Loader2, ShieldCheck } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { auth, authReady, db } from "../firebase";

const isValidPhone = (value) => /^0\d{9}$/.test(String(value || "").trim());
const TITLE_LOGO_SRC = "/uklogo.png";

const normalizeProfileName = (rawName, fallbackEmail = "") => {
  const clean = String(rawName || "").trim();
  if (clean) return clean;
  if (fallbackEmail) return String(fallbackEmail).split("@")[0] || "Admin";
  return "Admin";
};

export default function AdminLogin() {
  const navigate = useNavigate();

  const [isRegister, setIsRegister] = useState(false);

  const [identifier, setIdentifier] = useState("");
  const [fullName, setFullName] = useState("");
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [notice, setNotice] = useState("");

  useEffect(() => {
    let active = true;

    async function restoreAdminSession() {
      await authReady;
      const existingUser = auth.currentUser;
      if (!existingUser || !active) {
        return;
      }

      const snap = await getDoc(doc(db, "users", existingUser.uid));
      const profile = snap.exists() ? snap.data() : null;
      if (active && profile?.role === "admin" && profile?.approved === true) {
        navigate("/admin/dashboard", { replace: true });
      }
    }

    restoreAdminSession();

    return () => {
      active = false;
    };
  }, [navigate]);

  async function validateAdminAccessByUid(uid, fallbackError = "Admin account not found.") {
    const snap = await getDoc(doc(db, "users", uid));

    if (!snap.exists()) {
      await signOut(auth);
      throw new Error(fallbackError);
    }

    const profile = snap.data();

    if (profile?.role !== "admin") {
      await signOut(auth);
      throw new Error("This portal is for admin accounts only.");
    }

    if (profile?.approved !== true) {
      await signOut(auth);
      throw new Error("Your admin account is pending approval.");
    }

    return profile;
  }

  async function resolveEmailFromIdentifier(rawIdentifier) {
    const clean = String(rawIdentifier || "").trim();

    if (!clean) {
      throw new Error("Enter admin email or phone number.");
    }

    if (clean.includes("@")) {
      return clean;
    }

    if (!isValidPhone(clean)) {
      throw new Error("Use a valid email or 10-digit phone number (starts with 0).");
    }

    const phoneQuery = query(
      collection(db, "users"),
      where("phone", "==", clean),
      where("role", "==", "admin"),
      limit(1)
    );
    const snap = await getDocs(phoneQuery);

    if (snap.empty) {
      throw new Error("No admin account found with that phone number.");
    }

    const row = snap.docs[0].data();
    if (!row.email) {
      throw new Error("Admin account email is missing. Contact support.");
    }

    return String(row.email).trim();
  }

  async function handleLogin(event) {
    event.preventDefault();
    setError("");
    setNotice("");

    const cleanPassword = String(password || "").trim();
    if (!cleanPassword) {
      setError("Enter your password.");
      return;
    }

    setLoading(true);
    try {
      await authReady;
      const loginEmail = await resolveEmailFromIdentifier(identifier);
      const cred = await signInWithEmailAndPassword(auth, loginEmail, cleanPassword);
      await validateAdminAccessByUid(cred.user.uid);
      navigate("/admin/dashboard");
    } catch (submitError) {
      setError(submitError.message || "Unable to sign in.");
    } finally {
      setLoading(false);
    }
  }

  async function handleSignup(event) {
    event.preventDefault();
    setError("");
    setNotice("");

    const cleanEmail = String(email || "").trim().toLowerCase();
    const cleanPhone = String(phone || "").trim();
    const cleanPassword = String(password || "").trim();
    const cleanConfirmPassword = String(confirmPassword || "").trim();
    const cleanName = normalizeProfileName(fullName, cleanEmail);

    if (!cleanEmail || !cleanPhone || !cleanPassword || !cleanConfirmPassword || !cleanName) {
      setError("Complete all fields.");
      return;
    }

    if (!isValidPhone(cleanPhone)) {
      setError("Phone number must be 10 digits and start with 0.");
      return;
    }

    if (cleanPassword.length < 6) {
      setError("Password must be at least 6 characters.");
      return;
    }

    if (cleanPassword !== cleanConfirmPassword) {
      setError("Passwords do not match.");
      return;
    }

    setLoading(true);
    try {
      await authReady;

      const existingMethods = await fetchSignInMethodsForEmail(auth, cleanEmail);
      if (existingMethods.length > 0) {
        throw new Error("An account with this email already exists.");
      }

      const phoneSnap = await getDocs(query(collection(db, "users"), where("phone", "==", cleanPhone), limit(1)));
      if (!phoneSnap.empty) {
        throw new Error("This phone number is already in use.");
      }

      const cred = await createUserWithEmailAndPassword(auth, cleanEmail, cleanPassword);

      await setDoc(doc(db, "users", cred.user.uid), {
        email: cleanEmail,
        role: "admin",
        approved: false,
        paid: true,
        fullName: cleanName,
        businessName: cleanName,
        phone: cleanPhone,
        walletBalance: 0,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
        requestedAt: serverTimestamp(),
      });

      await signOut(auth);
      setIsRegister(false);
      setIdentifier(cleanEmail);
      setEmail("");
      setPhone("");
      setPassword("");
      setConfirmPassword("");
      setFullName("");
      setNotice("Admin account created. Wait for approval before login.");
    } catch (submitError) {
      setError(submitError.message || "Unable to create admin account.");
    } finally {
      setLoading(false);
    }
  }

  async function handleGoogleSignIn() {
    setError("");
    setNotice("");
    setLoading(true);

    try {
      await authReady;
      const provider = new GoogleAuthProvider();
      const cred = await signInWithPopup(auth, provider);
      const user = cred.user;
      const userRef = doc(db, "users", user.uid);
      const snap = await getDoc(userRef);

      if (!snap.exists()) {
        const mappedName = normalizeProfileName(user.displayName, user.email || "");
        await setDoc(userRef, {
          email: user.email || "",
          role: "admin",
          approved: false,
          paid: true,
          fullName: mappedName,
          businessName: mappedName,
          phone: user.phoneNumber || "",
          walletBalance: 0,
          createdAt: serverTimestamp(),
          updatedAt: serverTimestamp(),
          requestedAt: serverTimestamp(),
          authProvider: "google",
        });

        await signOut(auth);
        setNotice("Google admin request submitted. Wait for approval before portal access.");
        return;
      }

      await validateAdminAccessByUid(user.uid, "Admin account not found for this Google login.");
      navigate("/admin/dashboard");
    } catch (submitError) {
      setError(submitError.message || "Unable to sign in with Google.");
    } finally {
      setLoading(false);
    }
  }

  return (
    <div className="min-h-screen bg-[radial-gradient(circle_at_top,#dbe8ff_0%,#eef2ff_30%,#f5f6fa_65%,#eef1f5_100%)] p-4">
      <div className="mx-auto flex min-h-screen w-full max-w-md items-center justify-center">
        <motion.div
          initial={{ opacity: 0, y: 8 }}
          animate={{ opacity: 1, y: 0 }}
          className="w-full overflow-hidden rounded-[28px] border border-white/70 bg-white/90 shadow-[0_24px_80px_rgba(39,65,147,0.16)] backdrop-blur"
        >
          <div className="bg-[linear-gradient(135deg,#274193_0%,#3157c5_55%,#86a5ff_100%)] px-6 py-7 text-white">
            <div className="inline-flex items-center gap-2 rounded-full border border-white/20 bg-white/10 px-3 py-1 text-xs font-semibold uppercase tracking-[0.18em] text-white/90">
              <ShieldCheck size={14} /> Secure Admin
            </div>
            <img src={TITLE_LOGO_SRC} alt="UK Connect logo" className="mt-4 h-12 w-auto max-w-[200px] object-contain" />
            <p className="mt-1 max-w-xs text-sm text-blue-100">Sign in to manage transactions, agents, pricing, and network activity.</p>
          </div>

          <div className="p-6">
            <div className="mb-5">
              <div className="mb-3 flex items-center gap-2 rounded-2xl bg-[#eef3ff] p-1 text-sm font-semibold text-slate-600">
                <button
                  type="button"
                  onClick={() => {
                    setIsRegister(false);
                    setError("");
                    setNotice("");
                  }}
                  className={`flex-1 rounded-2xl px-4 py-2.5 transition ${
                    !isRegister ? "bg-white text-[#274193] shadow-sm" : "text-slate-500"
                  }`}
                >
                  Sign In
                </button>
                <button
                  type="button"
                  onClick={() => {
                    setIsRegister(true);
                    setError("");
                    setNotice("");
                  }}
                  className={`flex-1 rounded-2xl px-4 py-2.5 transition ${
                    isRegister ? "bg-white text-[#274193] shadow-sm" : "text-slate-500"
                  }`}
                >
                  Sign Up
                </button>
              </div>
              <p className="text-lg font-extrabold text-slate-900">{isRegister ? "Admin Sign Up" : "Admin Login"}</p>
              <p className="mt-1 text-sm text-slate-500">
                {isRegister
                  ? "Create an admin account request. Access is granted only after approval."
                  : "Sign in with email or phone number and password, or continue with Google."}
              </p>
            </div>

            {error && (
              <div className="mb-4 rounded-2xl border border-red-200 bg-red-50 p-3 text-sm font-semibold text-red-700">
                {error}
              </div>
            )}

            {notice && (
              <div className="mb-4 rounded-2xl border border-emerald-200 bg-emerald-50 p-3 text-sm font-semibold text-emerald-700">
                {notice}
              </div>
            )}

            {!isRegister ? (
              <form onSubmit={handleLogin} className="space-y-4">
                <div>
                  <label className="mb-1.5 block text-sm font-semibold text-slate-700">Email or Phone Number</label>
                  <input
                    type="text"
                    value={identifier}
                    onChange={(event) => setIdentifier(event.target.value)}
                    placeholder="admin@email.com or 024xxxxxxx"
                    className="w-full rounded-2xl border border-[#d4d9e4] bg-[#f8fafc] px-4 py-3 text-sm text-slate-900 outline-none transition focus:border-[#3157c5] focus:bg-white focus:ring-4 focus:ring-[#3157c5]/10"
                  />
                </div>

                <div>
                  <label className="mb-1.5 block text-sm font-semibold text-slate-700">Password</label>
                  <input
                    type="password"
                    value={password}
                    onChange={(event) => setPassword(event.target.value)}
                    placeholder="••••••••"
                    className="w-full rounded-2xl border border-[#d4d9e4] bg-[#f8fafc] px-4 py-3 text-sm text-slate-900 outline-none transition focus:border-[#3157c5] focus:bg-white focus:ring-4 focus:ring-[#3157c5]/10"
                  />
                </div>

                <button
                  type="submit"
                  disabled={loading}
                  className="flex w-full items-center justify-center gap-2 rounded-2xl bg-[linear-gradient(135deg,#274193_0%,#3157c5_100%)] px-4 py-3 text-sm font-bold text-white shadow-[0_12px_24px_rgba(49,87,197,0.28)] transition hover:brightness-105 disabled:opacity-60"
                >
                  {loading ? <Loader2 size={16} className="animate-spin" /> : null}
                  {loading ? "Signing In..." : "Sign In"}
                </button>

                <button
                  type="button"
                  onClick={handleGoogleSignIn}
                  disabled={loading}
                  className="flex w-full items-center justify-center gap-2 rounded-2xl border border-[#d4d9e4] bg-white px-4 py-3 text-sm font-bold text-slate-700 transition hover:bg-slate-50 disabled:opacity-60"
                >
                  {loading ? <Loader2 size={16} className="animate-spin" /> : null}
                  Continue with Google
                </button>
              </form>
            ) : (
              <form onSubmit={handleSignup} className="space-y-4">
                <div>
                  <label className="mb-1.5 block text-sm font-semibold text-slate-700">Full Name</label>
                  <input
                    type="text"
                    value={fullName}
                    onChange={(event) => setFullName(event.target.value)}
                    placeholder="Your full name"
                    className="w-full rounded-2xl border border-[#d4d9e4] bg-[#f8fafc] px-4 py-3 text-sm text-slate-900 outline-none transition focus:border-[#3157c5] focus:bg-white focus:ring-4 focus:ring-[#3157c5]/10"
                  />
                </div>

                <div>
                  <label className="mb-1.5 block text-sm font-semibold text-slate-700">Email</label>
                  <input
                    type="email"
                    value={email}
                    onChange={(event) => setEmail(event.target.value)}
                    placeholder="admin@email.com"
                    className="w-full rounded-2xl border border-[#d4d9e4] bg-[#f8fafc] px-4 py-3 text-sm text-slate-900 outline-none transition focus:border-[#3157c5] focus:bg-white focus:ring-4 focus:ring-[#3157c5]/10"
                  />
                </div>

                <div>
                  <label className="mb-1.5 block text-sm font-semibold text-slate-700">Phone Number</label>
                  <input
                    type="text"
                    value={phone}
                    onChange={(event) => setPhone(event.target.value)}
                    placeholder="024xxxxxxx"
                    maxLength={10}
                    className="w-full rounded-2xl border border-[#d4d9e4] bg-[#f8fafc] px-4 py-3 text-sm text-slate-900 outline-none transition focus:border-[#3157c5] focus:bg-white focus:ring-4 focus:ring-[#3157c5]/10"
                  />
                </div>

                <div>
                  <label className="mb-1.5 block text-sm font-semibold text-slate-700">Password</label>
                  <input
                    type="password"
                    value={password}
                    onChange={(event) => setPassword(event.target.value)}
                    placeholder="••••••••"
                    className="w-full rounded-2xl border border-[#d4d9e4] bg-[#f8fafc] px-4 py-3 text-sm text-slate-900 outline-none transition focus:border-[#3157c5] focus:bg-white focus:ring-4 focus:ring-[#3157c5]/10"
                  />
                </div>

                <div>
                  <label className="mb-1.5 block text-sm font-semibold text-slate-700">Confirm Password</label>
                  <input
                    type="password"
                    value={confirmPassword}
                    onChange={(event) => setConfirmPassword(event.target.value)}
                    placeholder="••••••••"
                    className="w-full rounded-2xl border border-[#d4d9e4] bg-[#f8fafc] px-4 py-3 text-sm text-slate-900 outline-none transition focus:border-[#3157c5] focus:bg-white focus:ring-4 focus:ring-[#3157c5]/10"
                  />
                </div>

                <button
                  type="submit"
                  disabled={loading}
                  className="flex w-full items-center justify-center gap-2 rounded-2xl bg-[linear-gradient(135deg,#274193_0%,#3157c5_100%)] px-4 py-3 text-sm font-bold text-white shadow-[0_12px_24px_rgba(49,87,197,0.28)] transition hover:brightness-105 disabled:opacity-60"
                >
                  {loading ? <Loader2 size={16} className="animate-spin" /> : null}
                  {loading ? "Submitting..." : "Create Admin Account"}
                </button>
              </form>
            )}
          </div>
        </motion.div>
      </div>
    </div>
  );
}